import { GetServerSideProps, GetStaticPaths } from 'next'

import { collectionsApi } from '@/entities/collection/api'
import { componentsApi } from '@/entities/component/api'
import { localesApi } from '@/entities/locale/api'
import { AnyPage } from '@/pages/_any-page-content.component'
import { env } from '@/shared/env'
import { PropsWithClassName } from '@/shared/interfaces'

import { AnyPageContext, usePageProps } from '../_app.page'

export default function LocalizedPage({ className }: PropsWithClassName) {
  const { page, global } = usePageProps()

  return (
    <AnyPage
      className={className}
      global={global}
      page={page}
    />
  )
}

export const getStaticProps: GetServerSideProps<AnyPageContext, { slug: string; localeOrSlug: string }> = async (ctx) => {
  const locales = await localesApi.getLocales()

  const paramsLocale = ctx.params!.localeOrSlug

  const isExistedLocaleValid = locales.some((locale) => locale.code === paramsLocale)
  if (!isExistedLocaleValid) {
    return {
      notFound: true,
    }
  }

  const locale = paramsLocale
  const hrefSlug = ctx.params!.slug

  const page = await collectionsApi.getPage({
    locale,
    href: {
      $endsWith: hrefSlug,
    },
  })

  if (!page) {
    return {
      notFound: true,
    }
  }

  const defaultLocale = locales.find((locale) => locale.isDefault)!.code
  if (paramsLocale === defaultLocale) {
    return {
      redirect: {
        destination: page.attributes.href,
        statusCode: 301,
      },
    }
  }

  const [navigation, bannerLinks, global] = await Promise.all([
    componentsApi.getNavigation({ locale }),
    componentsApi.getBannerLinks({ locale }),
    componentsApi.getGlobal({ locale }),
  ])

  return {
    props: {
      page,
      locales,
      navigation,
      bannerLinks,
      global,
    },
    revalidate: env.get<number>('PAGE_REVALIDATE'),
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const defaultLocale = await localesApi.getDefaultLocale()
  const pages = await collectionsApi.getAllPages({
    locale: {
      $not: defaultLocale.code,
    },
  })

  return {
    paths: pages.map((page) => ({
      params: {
        localeOrSlug: page.attributes.locale,
        slug: page.attributes.href.slice(1),
      },
    })),
    fallback: false,
  }
}
